import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import * as userStore from "../../../features/user/store/user.store";
import BriefList from "../../../features/brief-list/components/brief-list/brief-list.component";
import debounce from "../../../utils/debounce";
import { BriefType } from "../../../constants/types";
import { CampaignBriefTemplateLabels } from "../../../types/templates/campaign-brief/campaign-brief-template-labels.constant";
import { RESULTS_PER_PAGE } from "../../../utils/constants";
import { calculatePermissions } from "../../../types/user/permissions/ipermissions/ipermissions.functions";
import { getTotalPages } from "../../../components/Pagination/pagination.functions";
import { selectExecutionBriefOwnerId } from "../../../redux/selectors/executionBriefs";
import { selectRegionResults } from "../../../redux/selectors/commonSelectors";
import { selectWorkOrderOwnerId } from "../../../redux/selectors/workOrders";
import { setPermissions } from "../../../redux/actions/permissionsActions";
import { useURLQueryString } from "../../../hooks/useUrlQueryString";

import { createCampaignBriefPermissionsContext } from "../../campaign-brief/types/icampaign-brief-data.functions";
import { ICampaignBriefData } from "../../campaign-brief/types/campaign-brief-state/campaign-brief-state.interface";

import * as campaignBriefsStore from "../store/campaign-briefs.store";
import { mapCampaignBriefResultsToCampaignBriefProps } from "../types/campaign-brief-results.functions";
import { sortCampaignBriefPropsByCreatedAtDesc } from "../types/campaign-brief-props.functions";

import { CampaignBriefsBox } from "./campaign-briefs.styles";

export const CampaignBriefs = () => {
  const dispatch = useDispatch();

  const {
    values: { limit = [RESULTS_PER_PAGE], page = [1], ...parameters },
  } = useURLQueryString();

  const limitRef = useRef(limit);
  const pageRef = useRef(page);
  const paramsRef = useRef(parameters ?? {});
  const owners = useSelector(campaignBriefsStore.getCampaignBriefsOwnersOptions);
  const regions = useSelector(selectRegionResults) ?? [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((params) => campaignBriefsStore.fetchCampaignBriefs(params), 1000),
    []
  );

  useEffect(() => {
    if (!_.isEqual(paramsRef.current, parameters)) {
      if (_.isEqual(paramsRef.current, {}) || _.isEqual(parameters, {})) {
        campaignBriefsStore.fetchCampaignBriefs(parameters);
      } else {
        debouncedFetch(parameters);
      }
      paramsRef.current = parameters;
    } else if (!_.isEqual([limitRef.current, pageRef.current], [limit, page])) {
      limitRef.current = limit;
      pageRef.current = page;
      campaignBriefsStore.fetchCampaignBriefs({ limit, page, ...paramsRef.current }, true);
    }
  }, [debouncedFetch, dispatch, limit, page, parameters]);

  useEffect(() => {
    // get all campaigns
    campaignBriefsStore.fetchCampaignBriefsFilters();
    campaignBriefsStore.fetchCampaignBriefs({ limit, page, ...parameters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const campaignBriefList = useSelector(campaignBriefsStore.getCampaignBriefsResults);
  const resultCount = useSelector(campaignBriefsStore.getCampaignBriefsCount);
  const loading = useSelector(campaignBriefsStore.getLoadingSelector);

  const sortedDataList = sortCampaignBriefPropsByCreatedAtDesc(
    mapCampaignBriefResultsToCampaignBriefProps(campaignBriefList)
  );

  const user = useSelector(userStore.getUser);
  const executionOwnerId = useSelector(selectExecutionBriefOwnerId);
  const workorderOwnerId = useSelector(selectWorkOrderOwnerId);

  const [permissionsCreated, setPermissionsCreated] = useState<boolean>(false);

  useEffect(() => {
    const context = createCampaignBriefPermissionsContext(
      {} as ICampaignBriefData,
      user,
      executionOwnerId,
      workorderOwnerId
    );

    setPermissionsCreated(true);
    dispatch(setPermissions(calculatePermissions(context)));
  }, [dispatch, permissionsCreated, user, executionOwnerId, workorderOwnerId]);

  return (
    <CampaignBriefsBox>
      {sortedDataList && (
        <BriefList
          dataList={sortedDataList}
          briefType={BriefType.CAMPAIGN_BRIEF}
          filterOptionLists={{ users: owners, regions, templates: CampaignBriefTemplateLabels }}
          loading={loading}
          pageCount={getTotalPages(resultCount)}
        />
      )}
    </CampaignBriefsBox>
  );
};
